<template>
  <Teleport to="#navbarEnd">
    <div class="buttons">
      <button class="button" @click="openModal('viewEffects')">Bonuses</button>
      <button class="button is-primary" @click="giveInspiration(character.id)" :disabled="character.activeEffects.includes('inspiration')">Give Inspiration</button>
      <button class="button is-primary" @click="openModal('giveEquipment')">Give Equipment</button>
    </div>
  </Teleport>

  <div data-view="viewCharacter">
    <div class="columns is-variable is-6">
      <div class="column is-one-third-widescreen is-half-desktop is-one-quarter-fullhd">
        <Character :character="character" :computed="computed" />
      </div>

      <div class="column">
        <!-- Features -->
        <section v-if="featureCollection.length" class="block" id="featuresSection">
          <p class="label" style="margin-bottom: 1.5rem">Features</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in featureCollection" :key="card">
                <FeatureCard 
                  v-if="allFeatures[card].type === 'feature'"
                  class="disabledCard"
                  :feature="card"
                />
              </template>
            </CardGroup>
          </div>
        </section>

        <!-- Actions -->
        <section v-if="actionCollection.length" class="block" id="actionsSection">
          <p class="label" style="margin-bottom: 1.5rem">Actions</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in actionCollection" :key="card">
                <template v-if="allFeatures[card]">
                  <ActionCard
                    v-if="allFeatures[card].roll"
                    class="disabledCard"
                    check-icon="active"
                    :feature="card"
                    :value="character.activeEffects.includes(card)"
                    :disabled="allFeatures[card].requiresRested && character.restedAbilitiesUsed[card]"
                  />
                  <ActionCard 
                    v-else
                    class="disabledCard"
                    :feature="card"
                  />
                </template>
                <template v-else>
                  <ActionCard
                    v-if="allActions[card].roll"
                    class="disabledCard"
                    check-icon="active"
                    :action="card"
                    :value="character.activeEffects.includes(card)"
                    :disabled="allActions[card].requiresRested && character.restedAbilitiesUsed[card]"
                  />
                  <ActionCard
                    v-else
                    class="disabledCard"
                    :action="card"
                  />
                </template>
              </template>
            </CardGroup>
          </div>
        </section>

        <!-- Spells -->
        <section v-if="spellCollection.length" class="block" id="spellsSection">
          <p class="label" style="margin-bottom: 1.5rem">Spells</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in spellCollection" :key="card">
                <SpellCard
                  v-if="allSpells[card].damage || allSpells[card].bonus || allSpells[card].roll"
                  class="disabledCard"
                  :check-icon="allSpells[card].bonus?.concentrate ? 'concentrate' : 'check'"
                  :spell="card"
                  :value="character.activeEffects.includes(card)"
                  :disabled="!character.activeEffects.includes(card) && (character.restedAbilitiesUsed[card] || (character.spellPoints < allSpells[card].level))"
                />
                <SpellCard
                  v-else 
                  class="disabledCard" 
                  :spell="card"
                />
              </template>
            </CardGroup>
          </div>
        </section>

        <!-- Weapons -->
        <section v-if="weaponCollection.length" class="block" id="weaponsSection">
          <p class="label" style="margin-bottom: 1.5rem">Weapons</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in weaponCollection" :key="card">
                <EquipmentCard
                  v-if="allWeapons[card].armor"
                  class="disabledCard" 
                  :equipment="card"
                  :checkbox="true"
                  :disable-checkbox="true"
                  :true-value="card"
                  :value="character.equippedArmor"
                />
                <WeaponCard
                  v-else 
                  class="disabledCard"
                  :clickable="true"
                  :weapon="card"
                />
              </template>
            </CardGroup>
          </div>
          <!-- <div class="block">
            <button class="button">Add Weapon</button>
          </div> -->
        </section>

        <!-- Equipment -->
        <section v-if="equipmentCollection.length" class="block" id="equipmentSection">
          <p class="label" style="margin-bottom: 1.5rem">Equipment</p>
          <div class="content block">
            <CardGroup>
              <template v-for="card in equipmentCollection" :key="card">
                <!-- Equippable armor -->
                <EquipmentCard
                  v-if="allEquipment[card].armor"
                  class="disabledCard"
                  :equipment="card"
                  :checkbox="true"
                  :disable-checkbox="true"
                  :true-value="card"
                  :value="character.equippedArmor"
                />
                <!-- Useable -->
                <EquipmentCard
                  v-else-if="allEquipment[card].consumable || allEquipment[card].roll || allEquipment[card].bonus || allEquipment[card].requiresRested"
                  class="disabledCard"
                  check-icon="active"
                  :equipment="card"
                  :value="character.activeEffects.includes(card)"
                  :disabled="allEquipment[card].requiresRested && character.restedAbilitiesUsed[card]"
                />
                <!-- Everything else -->
                <EquipmentCard
                  v-else 
                  class="disabledCard" 
                  :equipment="card"
                />
              </template>
            </CardGroup>
          </div>
        </section>
      </div>
    </div>
  </div>

  <Teleport to="#modalContent">
    <!-- Manage effects -->
    <Modal name="viewEffects">
      <ModalCard>
        <template v-slot:title>Bonuses</template>
        
        <div class="table-container" style="font-size:90%;max-height:60vh;overflow-y:auto">
          <table class="table">
            <tbody>
              <template v-for="bonusDef in filteredBonuses" :key="bonusDef.name">
                <tr>
                  <th>{{ bonusDef.name }}</th>
                  <td>{{ plusOrMinus(bonusDef.value) }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </ModalCard>
    </Modal>

    <!-- Add equipment -->
    <Modal name="giveEquipment">
      <ModalCard>
        <template v-slot:title>Give Equipment</template>
        
        <div class="field has-addons has-addons-centered">
          <div class="control">
            <input v-model="equipmentFilter" type="text" class="input" placeholder="Filter by name">
          </div>
          <div class="control">
            <div class="select">
              <select v-model="selectedEquipment">
                <option :value="null" :selected="selectedEquipment === null" :disabled="true">- Select Equipment</option>
                <option v-for="item in filteredEquipment" :key="item.key" :value="item.key" :selected="selectedEquipment === item.key">{{ item.name }}</option>
              </select>
            </div>
          </div>
          <div class="control">
            <input v-model.number="equipmentQty" type="number" class="input isShort" :min="0" :max="Number.MAX_VALUE">
          </div>
        </div>

        <template v-slot:buttons>
          <button class="button is-primary" @click="terminateEquipmentModal" :disabled="!selectedEquipment || !equipmentQty">Give Equipment</button>
        </template>
      </ModalCard>
    </Modal>
  </Teleport>
</template>

<script>
import { defineComponent, computed, provide, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// Hooks & helpers
import characterComputedValues from '@/hooks/characterComputedValues'
import useModal from '@/hooks/useModal'
import plusOrMinus from '@/helpers/plusOrMinus'
import useToast from '@/hooks/useToast'
import { useHost, JOINED } from '@/hooks/room'

// Components
import Character from '@/components/Character'
import CardGroup from '@/components/CardGroup'
import FeatureCard from '@/components/cards/FeatureCard'
import ActionCard from '@/components/cards/ActionCard'
import WeaponCard from '@/components/cards/WeaponCard'
import EquipmentCard from '@/components/cards/EquipmentCard'
import SpellCard from '@/components/cards/SpellCard'
import Modal from '@/components/Modal'
import ModalCard from '@/components/ModalCard'

// Data
import allActions from '@/data/actions'
import allFeatures from '@/data/features'
import allWeapons from '@/data/weapons'
import allEquipment from '@/data/equipment'
import allSpells from '@/data/spells'
import allBonuses from '@/data/bonuses'

export default defineComponent({
  components: {
    Character,
    CardGroup,
    FeatureCard,
    ActionCard,
    WeaponCard,
    EquipmentCard,
    SpellCard,
    Modal,
    ModalCard
  },

  setup () {
    // State

    const route = useRoute()
    const router = useRouter()
    const { openModal, closeModal } = useModal()
    const toast = useToast()
    const { on, off, state: hostState, giveEquipment, giveInspiration } = useHost()

    if (hostState.status !== JOINED) {
      router.replace({ name: 'home' })
      return
    }

    // Character

    const character = computed(() => hostState.characters.find((c) => c.id === route.params.id))
    provide('character', character.value)

    if (!character.value) {
      router.replace({ name: 'home' })
      return
    }

    const computedCharacter = characterComputedValues(character.value)
    provide('computedCharacter', computedCharacter)

    // Collections

    const featureCollection = computed(() => ([
      ...character.value.features,
      ...(computedCharacter.race.value.features || []),
      ...(computedCharacter.characterClass.value.features || [])
    ].sort()))

    const actionCollection = computed(() => ([
      ...Object.keys(allActions),
      ...featureCollection.value.filter((key) => allFeatures[key].type !== 'feature')
    ].sort()))

    const spellCollection = computed(() => ([
      ...character.value.spells,
      ...(computedCharacter.race.value.spells || []),
      ...(computedCharacter.characterClass.value.spells || [])
    ].sort()))

    const weaponCollection = computed(() => ([
      ...character.value.weapons
    ].sort()))

    const equipmentCollection = computed(() => ([
      ...character.value.equipment
    ].sort()))

    // Give inspiration
    const giveInspirationHandler = (characterId) => {
      const character = hostState.characters.find((c) => c.id === characterId)
      toast(`Gave ${character.name} inspiration.`)
    }
    onMounted(() => {
      on('giveInspiration', giveInspirationHandler)
    })
    onUnmounted(() => {
      off(giveInspirationHandler)
    })

    // Give equipment modal
    const equipmentFilter = ref('')
    const selectedEquipment = ref(null)
    const equipmentQty = ref(1)
    const filteredEquipment = computed(() => {
      return Object.keys(allEquipment)
      .map((key) => {
        return {
          ...allEquipment[key],
          key
        }
      })
      .filter((item) => {
        return !item.armor
      })
      .filter((item) => {
        if (!equipmentFilter.value) return true
        return item.name.toLowerCase().includes(equipmentFilter.value.toLowerCase())
      })
    })

    watch(equipmentFilter, () => {
      selectedEquipment.value = null
    })
    
    const terminateEquipmentModal = () => {
      giveEquipment(character.value.id, selectedEquipment.value, allEquipment[selectedEquipment.value], equipmentQty.value)
      closeModal()
      equipmentFilter.value = ''
      selectedEquipment.value = null
      equipmentQty.value = 1
    }
    
    const giveEquipmentHandler = ({ characterId, itemId, qty }) => {
      const character = hostState.characters.find((c) => c.id === characterId)
      const item = allEquipment[itemId]
      toast(`Gave ${character.name} ${qty} ${item.name}.`)
    }
    onMounted(() => {
      on('giveEquipment', giveEquipmentHandler)
    })
    onUnmounted(() => {
      off(giveEquipmentHandler)
    })

    const filteredBonuses = computed(() => Object
      .keys(computedCharacter.bonuses.value)
      .map((key) => ({
        name: allBonuses[key].name,
        value: computedCharacter.bonuses.value[key]
      }))
      .filter((bonus) => !!bonus.value && bonus.name !== "")
    )

    return {
      hostState,
      filteredBonuses,

      // Modals
      openModal,

      // Methods
      plusOrMinus,
      giveInspiration,

      // Character
      character,
      computed: computedCharacter,
      ...computedCharacter,
      actionCollection,
      featureCollection,
      spellCollection,
      weaponCollection,
      equipmentCollection,

      // Data
      allActions,
      allFeatures,
      allEquipment,
      allWeapons,
      allSpells,
      allBonuses,

      // Equipment
      equipmentFilter,
      selectedEquipment,
      equipmentQty,
      filteredEquipment,
      terminateEquipmentModal
    }
  }
})
</script>

<style lang="sass" scoped>
.headerButtons
  .buttons
    margin: 0 0.5rem !important
    .button
      margin-bottom: 0 !important

  .menuButton::after
    content: '\e5d2'
    font-family: 'Material Icons'

.disabledCard
  pointer-events: none 
</style>
